import React, { useState, useEffect } from "react";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";

export default function PropertyAnalysisModal({
    initialMechanical,
    initialFunctional,
    initialApplication,
    onClose,
    onSave,
    readOnly = false
}) {
    const [mechanical, setMechanical] = useState(initialMechanical || {});
    const [functional, setFunctional] = useState(initialFunctional || {});
    const [application, setApplication] = useState(initialApplication || {});
    const [activeTab, setActiveTab] = useState("mechanical");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initialMechanical) setMechanical(initialMechanical);
        if (initialFunctional) setFunctional(initialFunctional);
        if (initialApplication) setApplication(initialApplication);
    }, [initialMechanical, initialFunctional, initialApplication]);

    const handleMechanicalChange = (e) => {
        if (readOnly) return;
        const { name, value } = e.target;
        setMechanical(prev => ({ ...prev, [name]: value === "" ? "" : value }));
        if (errors[`mech_${name}`]) setErrors(prev => ({ ...prev, [`mech_${name}`]: null }));
    };

    const handleFunctionalChange = (e) => {
        if (readOnly) return;
        const { name, value } = e.target;
        setFunctional(prev => ({ ...prev, [name]: value === "" ? "" : value }));
        if (errors[`func_${name}`]) setErrors(prev => ({ ...prev, [`func_${name}`]: null }));
    };

    const handleApplicationChange = (e) => {
        if (readOnly) return;
        const { name, value } = e.target;
        setApplication(prev => ({ ...prev, [name]: value === "" ? "" : value }));
        if (errors[`app_${name}`]) setErrors(prev => ({ ...prev, [`app_${name}`]: null }));
    };

    const blockInvalidChars = (e) => {
        if (["e", "E", "-", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const validate = () => {
        const newErrors = {};
        const isPositive = (val) => val !== "" && val !== null && Number(val) > 0;
        const isZero = (val) => val !== "" && val !== null && Number(val) === 0;
        const isNegative = (val) => val !== "" && val !== null && Number(val) < 0;

        // Mechanical Validation
        ["tensileStrength", "modulus", "elongationAtBreak"].forEach(field => {
            const val = mechanical[field];
            if (isZero(val)) newErrors[`mech_${field}`] = "cannot be zero";
            else if (isNegative(val)) newErrors[`mech_${field}`] = "must be positive";
        });

        // Functional Validation
        ["surfaceArea", "porosity", "permeability", "wettability", "electricalConductivity", "thermalConductivity"].forEach(field => {
            const val = functional[field];
            if (isZero(val)) newErrors[`func_${field}`] = "cannot be zero";
            else if (isNegative(val)) newErrors[`func_${field}`] = "must be positive";
        });

        // Application Validation
        ["filtrationEfficiency"].forEach(field => {
            const val = application[field];
            if (isZero(val)) newErrors[`app_${field}`] = "cannot be zero";
            else if (isNegative(val)) newErrors[`app_${field}`] = "must be positive";
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (readOnly) {
            onClose();
            return;
        }
        if (validate()) {
            if (onSave) {
                onSave({ mechanical, functional, application });
            }
            onClose();
        }
    };

    useModalKeyboardHandlers({
        onClose,
        onSave: () => onSave({ mechanical, functional, application }),
        dependencies: [mechanical, functional, application],
    });

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSubmit}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-bar-chart-steps me-2 text-primary"></i>
                            Fiber Properties
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-0">
                        {/* Tabs */}
                        <ul className="nav nav-tabs nav-fill bg-white border-bottom" role="tablist">
                            <li className="nav-item">
                                <button
                                    className={`nav-link py-3 fw-bold ${activeTab === "mechanical" ? "active text-primary border-bottom border-primary border-3" : "text-secondary"}`}
                                    type="button"
                                    onClick={() => setActiveTab("mechanical")}
                                >
                                    <i className="bi bi-hammer me-2"></i>
                                    Mechanical
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link py-3 fw-bold ${activeTab === "functional" ? "active text-primary border-bottom border-primary border-3" : "text-secondary"}`}
                                    type="button"
                                    onClick={() => setActiveTab("functional")}
                                >
                                    <i className="bi bi-gear-wide-connected me-2"></i>
                                    Functional
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link py-3 fw-bold ${activeTab === "application" ? "active text-primary border-bottom border-primary border-3" : "text-secondary"}`}
                                    type="button"
                                    onClick={() => setActiveTab("application")}
                                >
                                    <i className="bi bi-rocket-takeoff me-2"></i>
                                    Application
                                </button>
                            </li>
                        </ul>

                        <div className="p-4 bg-light-subtle">
                            {activeTab === "mechanical" && (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Tensile Strength (MPa)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="tensileStrength"
                                                    className={`form-control shadow-sm ${errors.mech_tensileStrength ? 'is-invalid border border-danger' : ''}`}
                                                    value={mechanical.tensileStrength ?? ""}
                                                    onChange={handleMechanicalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.mech_tensileStrength && <div className="text-danger small mt-1">{errors.mech_tensileStrength}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Modulus (MPa)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="modulus"
                                                    className={`form-control shadow-sm ${errors.mech_modulus ? 'is-invalid border border-danger' : ''}`}
                                                    value={mechanical.modulus ?? ""}
                                                    onChange={handleMechanicalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.mech_modulus && <div className="text-danger small mt-1">{errors.mech_modulus}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Elongation at Break (%)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="elongationAtBreak"
                                                    className={`form-control shadow-sm ${errors.mech_elongationAtBreak ? 'is-invalid border border-danger' : ''}`}
                                                    value={mechanical.elongationAtBreak ?? ""}
                                                    onChange={handleMechanicalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.mech_elongationAtBreak && <div className="text-danger small mt-1">{errors.mech_elongationAtBreak}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Fracture Behaviour</label>
                                                <select
                                                    name="fractureBehaviour"
                                                    className="form-select shadow-sm"
                                                    value={mechanical.fractureBehaviour ?? ""}
                                                    onChange={handleMechanicalChange}
                                                    disabled={readOnly}
                                                >
                                                    <option value="">-- Select Fracture Behaviour --</option>
                                                    <option value="Brittle fracture">Brittle fracture</option>
                                                    <option value="Ductile fracture">Ductile fracture</option>
                                                    <option value="Fibrillar (splitting) fracture">Fibrillar (splitting) fracture</option>
                                                    <option value="Interfacial / fiber pull-out–dominated fracture">Interfacial / fiber pull-out–dominated fracture</option>
                                                    <option value="Progressive network (pseudo-ductile) fracture">Progressive network (pseudo-ductile) fracture</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "functional" && (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Surface Area (m²/g)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="surfaceArea"
                                                    className={`form-control shadow-sm ${errors.func_surfaceArea ? 'is-invalid border border-danger' : ''}`}
                                                    value={functional.surfaceArea ?? ""}
                                                    onChange={handleFunctionalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.func_surfaceArea && <div className="text-danger small mt-1">{errors.func_surfaceArea}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Porosity (%)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="porosity"
                                                    className={`form-control shadow-sm ${errors.func_porosity ? 'is-invalid border border-danger' : ''}`}
                                                    value={functional.porosity ?? ""}
                                                    onChange={handleFunctionalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.func_porosity && <div className="text-danger small mt-1">{errors.func_porosity}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Permeability (nm²)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="permeability"
                                                    className={`form-control shadow-sm ${errors.func_permeability ? 'is-invalid border border-danger' : ''}`}
                                                    value={functional.permeability ?? ""}
                                                    onChange={handleFunctionalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.func_permeability && <div className="text-danger small mt-1">{errors.func_permeability}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Wettability (Contact Angle °)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="wettability"
                                                    className={`form-control shadow-sm ${errors.func_wettability ? 'is-invalid border border-danger' : ''}`}
                                                    value={functional.wettability ?? ""}
                                                    onChange={handleFunctionalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.func_wettability && <div className="text-danger small mt-1">{errors.func_wettability}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Electrical Conductivity (S/m)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="electricalConductivity"
                                                    className={`form-control shadow-sm ${errors.func_electricalConductivity ? 'is-invalid border border-danger' : ''}`}
                                                    value={functional.electricalConductivity ?? ""}
                                                    onChange={handleFunctionalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.func_electricalConductivity && <div className="text-danger small mt-1">{errors.func_electricalConductivity}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold text-secondary">Thermal Conductivity (W/m·K)</label>
                                                <input
                                                    type="number" step="any"
                                                    min="0"
                                                    name="thermalConductivity"
                                                    className={`form-control shadow-sm ${errors.func_thermalConductivity ? 'is-invalid border border-danger' : ''}`}
                                                    value={functional.thermalConductivity ?? ""}
                                                    onChange={handleFunctionalChange}
                                                    onKeyDown={blockInvalidChars}
                                                    disabled={readOnly}
                                                />
                                                {errors.func_thermalConductivity && <div className="text-danger small mt-1">{errors.func_thermalConductivity}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "application" && (
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-md-12">
                                                <label className="form-label fw-bold text-secondary">Primary Application</label>
                                                <select
                                                    name="applicationType"
                                                    className={`form-select shadow-sm ${errors.app_applicationType ? 'is-invalid border border-danger' : ''}`}
                                                    value={application.applicationType ?? ""}
                                                    onChange={handleApplicationChange}
                                                    disabled={readOnly}
                                                >
                                                    <option value="">-- Select Application Type --</option>
                                                    <option value="filtration">Filtration</option>
                                                    <option value="drug_delivery">Drug Delivery</option>
                                                    <option value="tissue_engineering">Tissue Engineering</option>
                                                    <option value="energy_storage">Energy Storage</option>
                                                    <option value="other">Other</option>
                                                </select>
                                                {errors.app_applicationType && <div className="text-danger small mt-1">{errors.app_applicationType}</div>}
                                            </div>

                                            {application.applicationType === "filtration" && (
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary">Filtration Efficiency (%)</label>
                                                    <input
                                                        type="number" step="any"
                                                        min="0"
                                                        name="filtrationEfficiency"
                                                        className={`form-control shadow-sm ${errors.app_filtrationEfficiency ? 'is-invalid border border-danger' : ''}`}
                                                        value={application.filtrationEfficiency ?? ""}
                                                        onChange={handleApplicationChange}
                                                        onKeyDown={blockInvalidChars}
                                                        disabled={readOnly}
                                                    />
                                                    {errors.app_filtrationEfficiency && <div className="text-danger small mt-1">{errors.app_filtrationEfficiency}</div>}
                                                </div>
                                            )}

                                            {application.applicationType === "drug_delivery" && (
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary border-bottom pb-1 mb-2 d-block">Drug Delivery Info</label>
                                                    <label className="form-label fw-bold text-secondary">Drug Release Kinetic</label>
                                                    <input
                                                        type="text"
                                                        name="drugReleaseKinetic"
                                                        className="form-control shadow-sm"
                                                        value={application.drugReleaseKinetic ?? ""}
                                                        onChange={handleApplicationChange}
                                                        disabled={readOnly}
                                                        placeholder="e.g. Zero-order, Fickian diffusion"
                                                    />
                                                </div>
                                            )}

                                            {application.applicationType === "tissue_engineering" && (
                                                <>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-bold text-secondary">Cell Adhesion</label>
                                                        <input
                                                            type="text"
                                                            name="cellAdhesion"
                                                            className="form-control shadow-sm"
                                                            value={application.cellAdhesion ?? ""}
                                                            onChange={handleApplicationChange}
                                                            disabled={readOnly}
                                                            placeholder="e.g. High, Moderate"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-bold text-secondary">Cell Proliferation</label>
                                                        <input
                                                            type="text"
                                                            name="cellProliferation"
                                                            className="form-control shadow-sm"
                                                            value={application.cellProliferation ?? ""}
                                                            onChange={handleApplicationChange}
                                                            disabled={readOnly}
                                                            placeholder="e.g. >90% viability at 24h"
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {application.applicationType === "energy_storage" && (
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary">Energy Storage Properties</label>
                                                    <input
                                                        type="text"
                                                        name="energyStorage"
                                                        className="form-control shadow-sm"
                                                        value={application.energyStorage ?? ""}
                                                        onChange={handleApplicationChange}
                                                        disabled={readOnly}
                                                        placeholder="e.g. 250 F/g capacitance"
                                                    />
                                                </div>
                                            )}

                                            {application.applicationType === "other" && (
                                                <div className="col-md-12">
                                                    <div className="alert alert-info py-2 small shadow-sm border-0 bg-white">
                                                        <i className="bi bi-info-circle-fill me-2 text-info"></i>
                                                        Please specify any other application-specific findings below.
                                                    </div>
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold text-secondary">Filtration Efficiency (%)</label>
                                                            <input type="number" step="any" name="filtrationEfficiency" className="form-control shadow-sm" value={application.filtrationEfficiency ?? ""} onChange={handleApplicationChange} disabled={readOnly} />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold text-secondary">Drug Release Kinetic</label>
                                                            <input type="text" name="drugReleaseKinetic" className="form-control shadow-sm" value={application.drugReleaseKinetic ?? ""} onChange={handleApplicationChange} disabled={readOnly} />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold text-secondary">Cell Adhesion</label>
                                                            <input type="text" name="cellAdhesion" className="form-control shadow-sm" value={application.cellAdhesion ?? ""} onChange={handleApplicationChange} disabled={readOnly} />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold text-secondary">Cell Proliferation</label>
                                                            <input type="text" name="cellProliferation" className="form-control shadow-sm" value={application.cellProliferation ?? ""} onChange={handleApplicationChange} disabled={readOnly} />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold text-secondary">Energy Storage</label>
                                                            <input type="text" name="energyStorage" className="form-control shadow-sm" value={application.energyStorage ?? ""} onChange={handleApplicationChange} disabled={readOnly} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        {!readOnly && (
                            <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                                <i className="bi bi-check2-circle me-1"></i>
                                Save Properties
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
